import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { apiService } from "../services/api";

// ============ TYPES ============

export interface NotificationData {
  id: string;
  type: "info" | "success" | "warning" | "error" | "system" | "task" | "reminder";
  title: string;
  message: string;
  timestamp: string; // ISO date string
  isRead: boolean;
  link?: string; // Screen ID or URL to navigate to
  isExternal?: boolean; // If true, opens link in new tab
  icon?: string; // Custom icon name
  metadata?: Record<string, unknown>; // Additional data
}

interface NotificationContextType {
  notifications: NotificationData[];
  unreadCount: number;
  isLoading: boolean;
  isOpen: boolean;
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  setNavigationCallback: (callback: (target: string) => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// ============ MOCK DATA FOR DEVELOPMENT ============

const mockNotifications: NotificationData[] = [
  {
    id: "notif-1",
    type: "task",
    title: "New Job Card Assigned",
    message: "Job Card #JC-2024-0892 has been assigned to you for vehicle KA-01-AB-1234.",
    timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 mins ago
    isRead: false,
    link: "102", // Job Card screen
  },
  {
    id: "notif-2",
    type: "warning",
    title: "Low Stock Alert",
    message: "Engine Oil 5W-30 is running low. Only 5 units remaining.",
    timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 mins ago
    isRead: false,
    link: "201", // Inventory screen
  },
  {
    id: "notif-3",
    type: "success",
    title: "Payment Received",
    message: "Payment of ₹15,500 received for Invoice #INV-2024-0456.",
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    isRead: false,
    link: "501", // Accounts screen
  },
  {
    id: "notif-4",
    type: "info",
    title: "Inspection Completed",
    message: "Vehicle inspection for MH-12-XY-5678 has been completed.",
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
    isRead: true,
    link: "103", // Inspection screen
  },
  {
    id: "notif-5",
    type: "reminder",
    title: "Service Reminder",
    message: "Vehicle DL-05-CD-9012 is due for scheduled maintenance tomorrow.",
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
    isRead: true,
  },
  {
    id: "notif-6",
    type: "system",
    title: "System Update",
    message: "New features have been added to the Card View. Check it out!",
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    isRead: true,
    link: "start-tour",
  },
];

// ============ API ENDPOINT CONFIGURATION ============

const NOTIFICATIONS_ENDPOINT = "/api/notifications";
const NOTIFICATIONS_STORAGE_KEY = "garage360-notifications-state";

// ============ HELPER FUNCTIONS ============

const getStoredNotifications = (): { readIds: string[] } => {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("[Notifications] Error reading from localStorage:", error);
  }
  return { readIds: [] };
};

const saveNotificationsState = (readIds: string[]) => {
  try {
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify({ readIds }));
  } catch (error) {
    console.error("[Notifications] Error saving to localStorage:", error);
  }
};

// ============ PROVIDER ============

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [navigationCallback, setNavigationCallbackState] = useState<((target: string) => void) | null>(null);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // Set navigation callback
  const setNavigationCallback = useCallback((callback: (target: string) => void) => {
    setNavigationCallbackState(() => callback);
  }, []);

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get<NotificationData[]>(NOTIFICATIONS_ENDPOINT);
      if (response && Array.isArray(response)) {
        // Apply local read state
        const { readIds } = getStoredNotifications();
        const notificationsWithReadState = response.map((n) => ({
          ...n,
          isRead: n.isRead || readIds.includes(n.id),
        }));
        setNotifications(notificationsWithReadState);
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.log("[Notifications] Backend not available, using mock data");
    }

    // Fallback to mock data
    const { readIds } = getStoredNotifications();
    const notificationsWithReadState = mockNotifications.map((n) => ({
      ...n,
      isRead: n.isRead || readIds.includes(n.id),
    }));
    setNotifications(notificationsWithReadState);
    setIsLoading(false);
  }, []);

  // Mark a notification as read
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    );

    // Save to localStorage
    const { readIds } = getStoredNotifications();
    if (!readIds.includes(notificationId)) {
      saveNotificationsState([...readIds, notificationId]);
    }

    // Optionally sync with backend
    try {
      apiService.post(`${NOTIFICATIONS_ENDPOINT}/${notificationId}/read`, {});
    } catch (error) {
      // Silent fail for backend sync
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

    // Save all IDs to localStorage
    const allIds = notifications.map((n) => n.id);
    saveNotificationsState(allIds);

    // Optionally sync with backend
    try {
      apiService.post(`${NOTIFICATIONS_ENDPOINT}/mark-all-read`, {});
    } catch (error) {
      // Silent fail for backend sync
    }
  }, [notifications]);

  // Remove a notification
  const removeNotification = useCallback((notificationId: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));

    // Optionally sync with backend
    try {
      apiService.delete(`${NOTIFICATIONS_ENDPOINT}/${notificationId}`);
    } catch (error) {
      // Silent fail for backend sync
    }
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);

    // Optionally sync with backend
    try {
      apiService.delete(`${NOTIFICATIONS_ENDPOINT}/clear-all`);
    } catch (error) {
      // Silent fail for backend sync
    }
  }, []);

  // Toggle dropdown open state
  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Set dropdown open state
  const setOpen = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        isOpen,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        toggleOpen,
        setOpen,
        setNavigationCallback,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// ============ HOOK ============

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};

// ============ EXPORTS ============

export type { NotificationContextType };
